import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaKey, FaUser } from 'react-icons/fa6';
import { MdOutlineEmail, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/button';
import { Error } from '../components/error';
import { Form } from '../components/form';
import { useAuthContext } from '../contexts/auth.context';
import { useFirebase } from '../hooks/firebase.hook';
import { useLayout } from '../hooks/layout.hook';
import { useNavigation } from '../hooks/navigation.hook';

export interface FormData {
  name?: string;
  mail: string;
  password: string;
}

export default function AuthScreen(): JSX.Element {
  const location = useLocation();
  const { t } = useTranslation();
  const { isUserLoading, signIn, signUp, sendPasswordReset } = useAuthContext();
  const { translateError } = useFirebase();
  const { navigate } = useNavigation();

  const [isSignUp, setIsSignUp] = useState(location.state?.isSignUp ?? true);
  const [error, setError] = useState<string>();
  const [showPassword, setShowPassword] = useState(false);

  const [isReset, setIsReset] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isResetDone, setIsResetDone] = useState(false);

  function handleSubmit({ name, mail, password }: FormData) {
    setError(undefined);
    setIsResetDone(false);

    if (isReset) {
      resetPassword(mail);
    } else {
      authenticate(name?.trim() ?? 'Anonymous', mail.toLowerCase(), password).catch((e) =>
        setError(translateError(e?.message)),
      );
    }
  }

  async function resetPassword(mail: string): Promise<void> {
    setIsResetting(true);

    return sendPasswordReset(mail)
      .then(() => {
        setIsReset(false);
        setIsResetDone(true);
      })
      .catch((error) => setError(translateError(error.message)))
      .finally(() => setIsResetting(false));
  }

  function authenticate(name: string, mail: string, password: string): Promise<void> {
    return isSignUp ? signUp(name, mail, password) : signIn(mail, password);
  }

  useLayout(
    () => ({
      hideFooter: true,
      backButton: true,
      title: isReset ? t('Reset password') : isSignUp ? t('Register') : t('Login'),
    }),
    [t, isReset, isSignUp],
  );

  return (
    <>
      <p className="text-center text-base-content/70 mb-5">
        {isReset ? (
          <>
            {t('Do you recall the password?')}{' '}
            <a className="link link-accent" onClick={() => setIsReset(false)}>
              {t('Login')}
            </a>
          </>
        ) : isSignUp ? (
          <>
            {t('You already have an account?')}{' '}
            <a className="link link-accent" onClick={() => setIsSignUp(false)}>
              {t('Login')}
            </a>
          </>
        ) : (
          <>
            {t("You don't have an account yet?")}{' '}
            <a className="link link-accent" onClick={() => setIsSignUp(true)}>
              {t('Register')}
            </a>
          </>
        )}
      </p>

      {isResetDone && (
        <div role="alert" className="alert alert-success alert-soft mb-4">
          <FaCheck />
          <span>{t('Reset email sent successfully!')}</span>
        </div>
      )}

      <Form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          {isSignUp && !isReset && (
            <div>
              <label className="input input-lg validator w-full">
                <FaUser className="text-base-content/70" />
                <input type="text" name="name" placeholder={t('Name')} required />
              </label>
              <div className="validator-hint hidden">{t('Required')}</div>
            </div>
          )}

          <div>
            <label className="input input-lg validator w-full">
              <MdOutlineEmail className="text-base-content/70" />
              <input type="email" name="mail" placeholder={t('Email')} required />
            </label>
            <div className="validator-hint hidden">{t('Please enter a valid email')}</div>
          </div>

          {!isReset && (
            <div>
              <label className="input input-lg validator w-full relative">
                <FaKey className="text-base-content/70" />
                <input type={showPassword ? 'text' : 'password'} name="password" placeholder="*****" required />

                <div
                  className="top-0 right-0 absolute h-full flex items-center cursor-pointer p-3"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <MdVisibility size={'1.5rem'} /> : <MdVisibilityOff size={'1.5rem'} />}
                </div>
              </label>
              <div className="validator-hint hidden">{t('Required')}</div>
            </div>
          )}

          {error && <Error msg={error} />}

          <Button className="btn btn-primary btn-lg" type="submit" loading={isUserLoading || isResetting}>
            {isReset ? t('Reset') : isSignUp ? t('Register') : t('Login')}
          </Button>
        </div>
      </Form>

      {!isReset && (
        <a
          className="link link-accent text-center mt-2"
          onClick={() => (isSignUp ? navigate('/privacy', { replace: false }) : setIsReset(true))}
        >
          {isSignUp ? t('Legal terms') : t('Forgot your password?')}
        </a>
      )}
    </>
  );
}
